import { useState, useCallback, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { WorkshopTemplate } from '../../interfaces/template.interface';
import { Search, Plus } from 'lucide-react';
import TemplateCard from './components/TemplateCard';
import AddTemplatePopup from './components/AddTemplatePopup';
import EditTemplatePopup from './components/EditTemplatePopup';
import AddFromTemplatePopup from './components/AddFromTemplatePopup';

const BibliothequePages = () => {
  const [templates, setTemplates] = useState<WorkshopTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<WorkshopTemplate | null>(null);
  const [addingFromTemplate, setAddingFromTemplate] = useState<WorkshopTemplate | null>(null);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, 'workshop_templates'));
      const data = snapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      })) as WorkshopTemplate[];

      setTemplates(data);
    } catch (error) {
      console.error('Erreur chargement templates :', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const filteredTemplates = templates.filter((t) =>
    t.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-4">Bibliothèque d'Ateliers</h1>

        <div className="flex gap-4 items-center">
          <div className="flex-1 relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              type="text"
              placeholder="Rechercher un template..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#623413]/40 focus:border-[#623413]"
            />
          </div>

          <button
            onClick={() => setShowAddPopup(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#623413] text-white rounded-lg hover:opacity-90 transition-opacity"
          >
            <Plus size={20} />
            Ajouter un template
          </button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse"
            >
              <div className="h-48 bg-gray-200" />
              <div className="p-4">
                <div className="h-6 bg-gray-200 rounded mb-2" />
                <div className="h-4 bg-gray-200 rounded w-3/4" />
              </div>
            </div>
          ))}
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="flex flex-col items-center justify-center flex-1 text-gray-500">
          <p className="text-lg">
            {searchQuery
              ? 'Aucun template trouvé pour cette recherche'
              : 'Aucun template disponible'}
          </p>
          {!searchQuery && (
            <button
              onClick={() => setShowAddPopup(true)}
              className="mt-4 text-[#623413] hover:underline"
            >
              Créer votre premier template
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onEdit={() => setEditingTemplate(template)}
              onAddToPlanning={() => setAddingFromTemplate(template)}
              onDeleted={fetchTemplates}
            />
          ))}
        </div>
      )}

      {showAddPopup && (
        <AddTemplatePopup
          onClose={() => setShowAddPopup(false)}
          onCreated={() => {
            fetchTemplates();
            setShowAddPopup(false);
          }}
        />
      )}

      {editingTemplate && (
        <EditTemplatePopup
          template={editingTemplate}
          onClose={() => setEditingTemplate(null)}
          onUpdated={() => {
            fetchTemplates();
            setEditingTemplate(null);
          }}
        />
      )}

      {addingFromTemplate && (
        <AddFromTemplatePopup
          template={addingFromTemplate}
          onClose={() => setAddingFromTemplate(null)}
          onCreated={() => {
            setAddingFromTemplate(null);
          }}
        />
      )}
    </div>
  );
};

export default BibliothequePages;
